import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { CommType } from '../types';
import { CuiLabel } from './CuiLabel';
import { CommsLog } from './CommsLog';
import styles from './CommsModal.module.css';

const HUMAN_TYPES: CommType[] = [
    'CALL',
    'EMAIL',
    'MEETING',
    'EXPEDITE_REQ',
    'ORDER_STOP',
    'TECHNICAL',
    'OTHER',
];

interface Props {
    lineId: number;
    title: string;
    onClose: () => void;
}

/** Shared comms modal: read the log + add a human comm. The free-text field carries
 *  the CUI-warning label (compliance boundary). */
export function CommsModal({ lineId, title, onClose }: Props) {
    const qc = useQueryClient();
    const commsQuery = useQuery({ queryKey: ['comms', lineId], queryFn: () => api.comms(lineId) });
    const [type, setType] = useState<CommType>('CALL');
    const [summary, setSummary] = useState('');

    const add = useMutation({
        mutationFn: () => api.addComm(lineId, type, summary.trim()),
        onSuccess: () => {
            setSummary('');
            void qc.invalidateQueries({ queryKey: ['comms', lineId] });
        },
    });

    return (
        <div
            className={styles.backdrop}
            role="dialog"
            aria-modal="true"
            aria-label={`Comms — ${title}`}
        >
            <div className={styles.modal}>
                <header className={styles.header}>
                    <strong>Comms · {title}</strong>
                    <button onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </header>

                <CommsLog rows={commsQuery.data?.rows ?? []} loading={commsQuery.isLoading} />

                <form
                    className={styles.form}
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (summary.trim()) add.mutate();
                    }}
                >
                    <CuiLabel />
                    <div className={styles.row}>
                        <select
                            aria-label="Comm type"
                            value={type}
                            onChange={(e) => setType(e.target.value as CommType)}
                        >
                            {HUMAN_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </div>
                    <textarea
                        aria-label="Comment summary"
                        placeholder="Brief summary (metadata only)…"
                        value={summary}
                        rows={3}
                        onChange={(e) => setSummary(e.target.value)}
                    />
                    <div className={styles.actions}>
                        <button className="primary" disabled={!summary.trim() || add.isPending}>
                            {add.isPending ? 'Logging…' : 'Log comm'}
                        </button>
                        {add.isError && (
                            <span className={styles.error}>Failed to log — try again.</span>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
